/**
 * Modulo gioco "Globe Guess".
 * - Scelta target 100% casuale tra TUTTI i paesi del database (nessuna esclusione).
 * - Globo senza confini né nomi: solo l'oceano/terra della texture.
 * - I paesi tentati restano colorati per tutta la partita.
 * - Aggiornamento incrementale (no rebuild ad ogni guess) tramite GameView.update().
 */

import type { BoardCallbacks, GameConfig, GameModule, GameView, GuessResult } from '@core/types';
import { dailyKey, seededIndex } from '@core/daily';
import { t } from '@core/i18n';
import {
  type Country,
  type CountriesData,
  type GlobeGuess,
  type GlobeState,
  cardinal,
  countryBearing,
  borderDistance,
  distanceColor,
  findCountry,
} from './logic';
import { createGlobe, type GlobeView3D } from './globe-view';

interface AssetCache {
  data: CountriesData;
  geo: { type: string; features: unknown[] };
}

let assetCache: AssetCache | null = null;

async function loadCountriesAssets(): Promise<AssetCache> {
  if (assetCache) return assetCache;
  const [dataRes, geoRes] = await Promise.all([
    fetch(import.meta.env.BASE_URL + 'data/countries.json'),
    fetch(import.meta.env.BASE_URL + 'data/countries.geo.json'),
  ]);
  if (!dataRes.ok || !geoRes.ok) throw new Error('Failed to load countries data');
  const data = (await dataRes.json()) as CountriesData;
  const geo = (await geoRes.json()) as AssetCache['geo'];
  assetCache = { data, geo };
  return assetCache;
}

function pickTarget(countries: Country[], daily: boolean): string {
  // Tutti i paesi sono ammissibili: nessun filtro.
  if (countries.length === 0) throw new Error('No countries available');
  if (daily) {
    const idx = seededIndex(`globe::${dailyKey()}`, countries.length);
    return countries[idx].iso3;
  }
  return countries[Math.floor(Math.random() * countries.length)].iso3;
}

export const globeGame: GameModule<GlobeState, GlobeGuess> = {
  id: 'globe',
  metadata: {
    name: t('globe.name'),
    description: t('globe.description'),
    icon: '🌍',
    iconUrl: 'images/loghi/globe.png',
    minPlayers: 1,
    maxPlayers: 8,
    supportsTeams: true,
    supportsDaily: true,
  },

  async loadAssets() {
    await loadCountriesAssets();
  },

  initState(config: GameConfig): GlobeState {
    if (!assetCache) throw new Error('Assets not loaded; call loadAssets() first');
    const targetIso3 = pickTarget(assetCache.data.countries, config.daily);
    return {
      targetIso3,
      guesses: [],
      distancesKm: {},
      bearings: {},
      daily: config.daily,
    };
  },

  validateGuess(_state, raw): GlobeGuess | null {
    if (!assetCache) return null;
    const country = findCountry(raw, assetCache.data.countries);
    if (!country) return null;
    return { iso3: country.iso3, cca2: country.cca2, name: country.name, flag: country.flag };
  },

  isDuplicateGuess(state, guess) {
    return state.guesses.includes(guess.iso3);
  },

  surrender(state) {
    return { ...state, surrendered: true };
  },

  isGameOver(state) {
    return state.surrendered === true || state.guesses.includes(state.targetIso3);
  },

  getTargetLabel(state) {
    if (!assetCache) return null;
    const c = assetCache.data.countries.find((cc) => cc.iso3 === state.targetIso3);
    return c?.name ?? null;
  },

  applyGuess(state, guess) {
    if (!assetCache) throw new Error('Assets not loaded');
    const list = assetCache.data.countries;
    const guessed = list.find((c) => c.iso3 === guess.iso3)!;
    const target = list.find((c) => c.iso3 === state.targetIso3)!;
    const km = Math.round(
      borderDistance(
        guessed,
        target,
        assetCache.geo.features as { properties: Record<string, unknown>; geometry: unknown }[],
      ),
    );
    const bear = countryBearing(guessed, target);
    const next: GlobeState = {
      ...state,
      guesses: state.guesses.includes(guess.iso3) ? state.guesses : [...state.guesses, guess.iso3],
      distancesKm: { ...state.distancesKm, [guess.iso3]: km },
      bearings: { ...state.bearings, [guess.iso3]: bear },
    };
    const correct = guess.iso3 === state.targetIso3;
    // bordersTarget: distanza 0 senza vittoria = il paese tentato confina con il target
    const bordersTarget = !correct && km === 0;
    const result: GuessResult = {
      correct,
      score: km,
      feedback: correct
        ? t('win.title')
        : [
            `${guess.name} • ${t('globe.distance', { km: km.toLocaleString() })} • ${t('globe.bearing.' + cardinal(bear))}`,
            bordersTarget ? t('globe.borders_target') : null,
          ]
            .filter(Boolean)
            .join(' • '),
      meta: { iso3: guess.iso3, color: distanceColor(km), bearing: bear },
    };
    return { state: next, result };
  },

  isWon(state) {
    return state.guesses.includes(state.targetIso3);
  },

  renderBoard(container, state, _callbacks: BoardCallbacks<GlobeGuess>): GameView {
    container.innerHTML = '';
    const loading = document.createElement('div');
    loading.className = 'absolute inset-0 grid place-items-center text-slate-400';
    loading.textContent = t('globe.loading');
    container.append(loading);

    let view: GlobeView3D | null = null;
    let destroyed = false;
    let revealed = false;
    const appliedIso = new Set<string>();

    function applyState(s: GlobeState): void {
      if (!view || !assetCache) return;
      for (const iso of s.guesses) {
        if (appliedIso.has(iso)) continue;
        const km = s.distancesKm[iso] ?? 0;
        const c = assetCache.data.countries.find((cc) => cc.iso3 === iso);
        view.highlight(iso, distanceColor(km), c?.centroid);
        appliedIso.add(iso);
      }
      // Focus sull'ultimo tentativo
      const lastIso = s.guesses[s.guesses.length - 1];
      if (lastIso && !s.surrendered) {
        const c = assetCache.data.countries.find((cc) => cc.iso3 === lastIso);
        if (c) view.focus(c);
      }
      // Rivela il target se la partita è finita (per vittoria o resa).
      const isOver = s.surrendered === true || s.guesses.includes(s.targetIso3);
      if (isOver && !revealed) {
        const target = assetCache.data.countries.find((cc) => cc.iso3 === s.targetIso3);
        if (target) {
          // Heuristic: se il target era già nei guesses (vinto), il poligono è già evidenziato;
          // riveliamo comunque per portare il colore verde + focus.
          const hasPolygon = !!features.find((f) => {
            const p = (f as { properties?: { ISO_A3?: string; ADM0_A3?: string } }).properties;
            return (p?.ISO_A3 ?? p?.ADM0_A3) === target.iso3;
          });
          view.reveal(target, hasPolygon);
          revealed = true;
        }
      }
    }

    let features: Parameters<typeof createGlobe>[0]['geoFeatures'] = [];

    void (async () => {
      const cache = await loadCountriesAssets();
      if (destroyed) return;
      features = cache.geo.features as Parameters<typeof createGlobe>[0]['geoFeatures'];
      view = await createGlobe({
        container,
        geoFeatures: features,
      });
      loading.remove();
      applyState(state);
    })();

    return {
      update(nextState) {
        applyState(nextState as GlobeState);
      },
      focusCountry(iso3: string) {
        if (!view || !assetCache) return;
        const c = assetCache.data.countries.find((cc) => cc.iso3 === iso3);
        if (c) view.focus(c);
      },
      destroy() {
        destroyed = true;
        view?.destroy();
        container.innerHTML = '';
      },
    };
  },

  serializeState(state) {
    return state;
  },

  deserializeState(raw) {
    return raw as GlobeState;
  },
};
